import React, { useState } from 'react';
import { Gift, Clock, Users, MapPin } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const OfferCard = ({ offer, onClaim, onRedeem, showStats = false }) => {
  const { theme } = useTheme();
  const { userRole } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      if (action === 'claim') {
        await onClaim(offer.id);
        toast.success('Offer claimed successfully!');
      } else if (action === 'redeem') {
        await onRedeem(offer.id);
        toast.success('Offer redeemed successfully!');
      }
    } catch (error) {
      toast.error('Action failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isExpired = new Date(offer.expiryDate) < new Date();
  const isActive = offer.status === 'active';
  const canClaim = userRole === 'customer' && isActive && !isExpired && !offer.claimed;
  const canRedeem = userRole === 'customer' && offer.claimed && !offer.redeemed && !isExpired;

  return (
    <div className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden ${
      isExpired ? 'opacity-60' : ''
    }`}>
      {/* Offer Header */}
      <div className={`${theme.gradient} p-4 text-white`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <Gift className="w-5 h-5" />
            <h3 className="font-semibold text-lg">{offer.title}</h3>
          </div>
          {isExpired && (
            <span className="bg-red-500 text-xs px-2 py-1 rounded-full">
              Expired
            </span>
          )}
        </div>
        <p className="text-sm opacity-90 mt-1">{offer.description}</p>
      </div>

      {/* Offer Content */}
      <div className="p-4">
        <div className="space-y-3">
          {/* Discount/Points Info */}
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Value:</span>
            <span className="font-semibold text-lg">
              {offer.discountType === 'percentage' 
                ? `${offer.discountValue}% OFF`
                : offer.discountType === 'fixed'
                ? `₹${offer.discountValue} OFF`
                : `${offer.pointsRequired} Points`
              }
            </span>
          </div>

          {/* Minimum Purchase */}
          {offer.minimumPurchase && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Min. Purchase:</span>
              <span className="font-medium">₹{offer.minimumPurchase}</span>
            </div>
          )}

          {/* Points Required */}
          {offer.pointsRequired && (
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Points Required:</span>
              <span className="font-medium">{offer.pointsRequired} pts</span>
            </div>
          )}

          {/* Expiry Date */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-1 text-gray-500">
              <Clock className="w-4 h-4" />
              <span>Expires:</span>
            </div>
            <span className={`font-medium ${isExpired ? 'text-red-500' : 'text-gray-700'}`}>
              {new Date(offer.expiryDate).toLocaleDateString()}
            </span>
          </div>

          {/* Business Stats (for business users) */}
          {showStats && userRole === 'business' && (
            <div className="border-t pt-3 mt-3">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-1 text-gray-500">
                  <Users className="w-4 h-4" />
                  <span>Used by:</span>
                </div>
                <span className="font-medium">{offer.usedCount || 0} customers</span>
              </div>
            </div>
          )}

          {/* Shop Location (for customer view) */}
          {offer.shopName && userRole === 'customer' && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-1 text-gray-500">
                <MapPin className="w-4 h-4" />
                <span>Shop:</span>
              </div>
              <span className="font-medium">{offer.shopName}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {userRole === 'customer' && (
          <div className="mt-4 space-y-2">
            {canClaim && (
              <button
                onClick={() => handleAction('claim')}
                disabled={loading}
                className={`w-full ${theme.button} py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading ? 'Claiming...' : 'Claim Offer'}
              </button>
            )}
            
            {canRedeem && (
              <button
                onClick={() => handleAction('redeem')}
                disabled={loading}
                className={`w-full ${theme.button} py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading ? 'Redeeming...' : 'Redeem Now'}
              </button>
            )}

            {offer.claimed && !canRedeem && !offer.redeemed && (
              <div className="text-center py-2 text-green-600 font-medium">
                ✓ Claimed - Ready to redeem
              </div>
            )}

            {offer.redeemed && (
              <div className="text-center py-2 text-gray-500 font-medium">
                ✓ Redeemed
              </div>
            )}

            {!canClaim && !canRedeem && !offer.claimed && !isExpired && (
              <div className="text-center py-2 text-gray-500">
                Not eligible for this offer
              </div>
            )}
          </div>
        )}

        {/* Business View Actions */}
        {userRole === 'business' && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Status: {offer.status}</span>
              <span>Created: {new Date(offer.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfferCard;
